/** 昼夜主题：html.app-dark + 深色变量集（见 src/assets/dark-mode.css、src/assets/theme/cm-tokens.css） */

import { ref } from 'vue'
import { dispatchEchartsThemeRefresh } from '@/utils/chart-theme-colors'

const STORAGE_KEY = 'app-theme'
const CHANGE_EVENT = 'app-theme-change'

/** 实际生效的主题（响应式，供 computed 依赖切换暗色资源等） */
export type AppTheme = 'light' | 'dark'

/** 用户选择的主题模式：浅色 / 深色 / 跟随系统 */
export type AppThemeMode = AppTheme | 'system'

/** 当前生效主题（响应式） */
export const themeRef = ref<AppTheme>('light')
/** 当前选择的主题模式（响应式） */
export const themeModeRef = ref<AppThemeMode>('system')

export function getTheme (): AppTheme {
  return themeRef.value
}

export function getThemeMode (): AppThemeMode {
  return themeModeRef.value
}

function applyTheme (theme: AppTheme) {
  themeRef.value = theme
  document.documentElement.classList.toggle('app-dark', theme === 'dark')
  // 图表（ECharts canvas）不感知 CSS 变量变化，主题切换后派发事件重绘取深色主色
  dispatchEchartsThemeRefresh()
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: theme }))
}

/** 跟随系统：由系统深浅色偏好解析实际主题 */
function resolveSystemTheme (): AppTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyThemeMode (mode: AppThemeMode) {
  themeModeRef.value = mode
  applyTheme(mode === 'system' ? resolveSystemTheme() : mode)
}

export function setThemeMode (mode: AppThemeMode) {
  localStorage.setItem(STORAGE_KEY, mode)
  applyThemeMode(mode)
}

function isAppThemeMode (raw: string | null): raw is AppThemeMode {
  return raw === 'light' || raw === 'dark' || raw === 'system'
}

/** 初始化：无本地记录时默认跟随系统；system 模式下系统深浅色变化实时生效 */
export function initAppTheme () {
  const raw = localStorage.getItem(STORAGE_KEY)
  applyThemeMode(isAppThemeMode(raw) ? raw : 'system')

  const media = window.matchMedia('(prefers-color-scheme: dark)')
  const handleSystemThemeChange = () => {
    if (themeModeRef.value === 'system') {
      applyTheme(resolveSystemTheme())
    }
  }
  if (media.addEventListener) {
    media.addEventListener('change', handleSystemThemeChange)
  } else if (media.addListener) {
    media.addListener(handleSystemThemeChange)
  }
}

export function onAppThemeChange (handler: (theme: AppTheme) => void) {
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<AppTheme>).detail
    handler(detail ?? getTheme())
  }
  window.addEventListener(CHANGE_EVENT, listener)
  return () => window.removeEventListener(CHANGE_EVENT, listener)
}
